import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { GovernmentOrgPicker } from '../_shared/components/GovernmentOrgPicker';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { useGovernmentOrgCatalog } from '../Programs/referenceDataHooks';
import { vehicleTypeLabel } from './contractVehicleUi';
import { contractVehicleHooks } from './hooks';
const vehicleTypeOptions = [
    {
        value: 'GSA_SCHEDULE',
        label: 'GSA Schedule'
    },
    {
        value: 'GWAC',
        label: 'GWAC'
    },
    {
        value: 'BPA',
        label: 'BPA'
    },
    {
        value: 'IDIQ',
        label: 'IDIQ'
    },
    {
        value: 'MAC',
        label: 'MAC'
    },
    {
        value: 'OTHER',
        label: 'Other'
    }
];
const statusOptions = [
    {
        value: 'ACTIVE',
        label: 'Active'
    },
    {
        value: 'INACTIVE',
        label: 'Inactive'
    }
];
const columns = [
    {
        key: 'name',
        header: 'Name'
    },
    {
        key: 'vehicleType',
        header: 'Type',
        render: (row)=>vehicleTypeLabel(row.vehicleType)
    },
    {
        key: 'agencyAcronym',
        header: 'Agency'
    },
    {
        key: 'contractNumber',
        header: 'Contract #'
    },
    {
        key: 'status',
        header: 'Status',
        render: (row)=>row.status || 'ACTIVE'
    }
];
// The awarding agency is deliberately NOT a declarative field: it's picked
// from the gov-org registry (dropdown, not free text) via extraContent /
// editControl below, and persists as the acronym string until the
// awarding_organization_id FK lands with W2-A1 (see the Phase 1 design
// record in major-push/lcat-intelligence/02-phase-1-registries-ia.md).
const fields = [
    {
        kind: 'text',
        name: 'name',
        label: 'Vehicle name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'select',
        name: 'vehicleType',
        label: 'Type',
        required: true,
        options: vehicleTypeOptions
    },
    {
        kind: 'text',
        name: 'contractNumber',
        label: 'Contract number',
        maxLength: 100
    },
    {
        kind: 'select',
        name: 'status',
        label: 'Status',
        required: true,
        options: statusOptions
    },
    {
        kind: 'textarea',
        name: 'description',
        label: 'Description',
        rows: 3
    }
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const vehicleDefaults = (v)=>({
        id: v?.id ?? '',
        name: v?.name ?? '',
        vehicleType: v?.vehicleType ?? 'GWAC',
        contractNumber: v?.contractNumber ?? '',
        status: v?.status ?? 'ACTIVE',
        description: v?.description ?? ''
    });
// Read sections plus, per row, the inline-edit control. Every field the create
// form exposes carries an `editField` (the SAME config), so edit-in-place and
// create stay in lockstep. The awarding agency is the exception: its edit
// control is the gov-org registry picker, which needs live component state,
// so the caller injects it (same pattern as ProgramCompaniesTab's company
// control).
function vehicleDetailSections(v, agencyEditControl) {
    return [
        {
            heading: 'Vehicle',
            rows: [
                {
                    label: 'Vehicle name',
                    value: v.name || '—',
                    editField: {
                        kind: 'text',
                        name: 'name',
                        label: 'Vehicle name',
                        required: true,
                        maxLength: 200
                    }
                },
                {
                    label: 'Type',
                    value: vehicleTypeLabel(v.vehicleType),
                    editField: {
                        kind: 'select',
                        name: 'vehicleType',
                        label: 'Type',
                        required: true,
                        options: vehicleTypeOptions
                    }
                },
                {
                    label: 'Awarding agency',
                    value: v.agencyAcronym || '—',
                    editControl: agencyEditControl
                },
                {
                    label: 'Contract number',
                    value: v.contractNumber || '—',
                    editField: {
                        kind: 'text',
                        name: 'contractNumber',
                        label: 'Contract number',
                        maxLength: 100
                    }
                },
                {
                    label: 'Status',
                    value: v.status || 'ACTIVE',
                    editField: {
                        kind: 'select',
                        name: 'status',
                        label: 'Status',
                        required: true,
                        options: statusOptions
                    }
                },
                {
                    label: 'Description',
                    value: v.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        rows: 3
                    }
                }
            ]
        }
    ];
}
export function ContractVehiclesTab() {
    const tenantId = useTenantId();
    const { data, isLoading } = contractVehicleHooks.useList({
        companyId: tenantId
    });
    const upsert = contractVehicleHooks.useUpsert();
    const remove = contractVehicleHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list without an effect that re-syncs
    // state (which infinite-loops on fresh-but-equal references and hangs).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((v)=>v.id === detailId) ?? null : null;
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    // Awarding agency rides the gov-org registry picker (parent state, not RHF —
    // same pattern as ProgramCompaniesTab's company control). Until the
    // awarding_organization_id FK lands (W2-A1 rider), what PERSISTS is the
    // picked org's acronym; the catalog resolves id → acronym at submit.
    const catalog = useGovernmentOrgCatalog();
    const [awardingOrgId, setAwardingOrgId] = useState('');
    const acronymFor = (orgId)=>{
        const org = catalog.byId.get(orgId);
        if (!org) return '';
        return (org.acronym || org.name).slice(0, 20);
    };
    // Legacy rows predate the picker and carry only the acronym string; a
    // case-insensitive reverse lookup preseeds the picker when it can. It
    // matches the SAME derivation acronymFor persists (acronym, else truncated
    // name) so a round-trip through an acronym-less org still preselects.
    const orgIdForAcronym = (stored)=>{
        if (!stored) return '';
        const lower = stored.toLowerCase();
        for (const org of catalog.byId.values()){
            if ((org.acronym || org.name).slice(0, 20).toLowerCase() === lower) return org.id;
        }
        return '';
    };
    const agencyPicker = /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
        id: "vehicle-awarding-org",
        label: "Awarding agency",
        helpText: "Which organization administers this vehicle (GSA for MAS, VA for T4NG…). Picked from the Government Orgs registry.",
        value: awardingOrgId,
        onChange: setAwardingOrgId,
        testid: Testids.deliveryRegistries.vehicleAgencyOrgPicker
    });
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). fallbackAcronym preserves a legacy free-text acronym
    // the reverse lookup couldn't map when the user saves without repicking.
    const submitVehicle = async (values, id, fallbackAcronym)=>{
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            name: values.name,
            vehicleType: values.vehicleType,
            contractNumber: values.contractNumber ?? '',
            agencyAcronym: awardingOrgId ? acronymFor(awardingOrgId) : fallbackAcronym,
            status: values.status ?? 'ACTIVE',
            description: values.description ?? ''
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    const detailEdit = detail ? {
        defaultValues: vehicleDefaults(detail),
        onSubmit: (v)=>submitVehicle(v, detail.id, detail.agencyAcronym ?? ''),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>setAwardingOrgId(orgIdForAcronym(detail.agencyAcronym ?? ''))
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "text-sm text-muted-foreground",
                children: [
                    "Tenant contract vehicles and IDIQs. Seed defaults locally with",
                    ' ',
                    /*#__PURE__*/ _jsx("code", {
                        className: "text-xs",
                        children: "yarn ui seed registries"
                    }),
                    "."
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>{
                        // Reset here, not in onOpenChange: Radix only fires onOpenChange
                        // for its own dismissal events, never for this parent-driven
                        // open, so a reset there is dead code and a prior edit's picker
                        // value would leak into the new vehicle (review finding, #977).
                        setAwardingOrgId('');
                        setFormOpen(true);
                    },
                    "data-testid": Testids.deliveryRegistries.vehicleCreate,
                    children: "New Contract Vehicle"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryRegistries.vehiclesTable,
                rowTestid: Testids.deliveryRegistries.vehicleRow,
                rowKey: (v)=>v.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRegistries.vehicleEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryRegistries.vehicleDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Contract Vehicle",
                fields: fields,
                defaultValues: vehicleDefaults(null),
                extraContent: agencyPicker,
                onSubmit: async (v)=>{
                    await submitVehicle(v, '', '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.name || 'Contract Vehicle' : '',
                sections: detail ? vehicleDetailSections(detail, agencyPicker) : [],
                testid: Testids.deliveryRegistries.vehicleDetailDrawer,
                edit: detailEdit,
                canEdit: true,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Contract Vehicle",
                description: deleting ? `Permanently delete "${deleting.name}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
